import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice} from "@reduxjs/toolkit";
import type ConstructionAllDetailsGuiDto from "../../dto/construction/ConstructionAllDetailsGuiDto";
import type {IDefaultConstructionCalculationParameters} from "../../types/IDefaultConstructionCalculationParameters";
import type {EditedProperty} from "../../types/EditedProperty";
import type {SelectedEntity} from "./types/SelectedEntity";
import {
  getAllConstructionElements,
  getChildConstructionElements,
} from "./constructionNewSliceHelpers";
import type TagDto from "../../dto/construction/TagDto";
import type ConstructionListItem from "../constructionsNew/types/ConstructionListItem";

export interface ConstructionStateNew {
  loading: boolean;
  data: ConstructionAllDetailsGuiDto | null;
  editedProperties: EditedProperty[];
  deselectedEntities: SelectedEntity[];
  constructionElements: SelectedEntity[];
  defaultCalculationParams: IDefaultConstructionCalculationParameters | null;
  latestCalculationId: string | undefined;
  nextId: string | null;
  prevId: string | null;
}

export interface LoadPayloadAction {
  currentConstruction: ConstructionAllDetailsGuiDto | null;
  latestCalculationId: string | undefined;
  all: ConstructionListItem[];
  defaultCalculationParams: IDefaultConstructionCalculationParameters | null;
}

const initialState: ConstructionStateNew = {
  loading: true,
  data: null,
  editedProperties: [],
  deselectedEntities: [],
  constructionElements: [],
  defaultCalculationParams: null,
  latestCalculationId: undefined,
  nextId: null,
  prevId: null,
};

const loadConstruction = (state: ConstructionStateNew, {payload}: PayloadAction<LoadPayloadAction>): void => {
  if (payload === undefined || payload === null) {
    state.loading = false;
    return;
  }
  state.data = payload.currentConstruction;
  state.latestCalculationId = payload.latestCalculationId;
  state.defaultCalculationParams = payload.defaultCalculationParams;

  const currentIndex = payload.all?.findIndex(c => c.id === payload.currentConstruction?.id);
  if (currentIndex !== undefined) {
    state.prevId = payload.all[currentIndex - 1]?.id ?? null;
    state.nextId = payload.all[currentIndex + 1]?.id ?? null;
  }

  state.constructionElements = getAllConstructionElements(state.data?.constructionParts);
  state.deselectedEntities = state.constructionElements.filter(ce => !ce.isSelected);

  state.loading = false;
};

const clearConstruction = (state: ConstructionStateNew): void => {
  state.loading = true;
  state.data = null;
  state.editedProperties = [];
  state.deselectedEntities = [];
  state.constructionElements = [];
  state.defaultCalculationParams = null;
  state.latestCalculationId = undefined;
  state.nextId = null;
  state.prevId = null;
};

const updateEditablePropsConstruction = (
  state: ConstructionStateNew,
  {payload}: PayloadAction<EditedProperty>,
): void => {
  state.editedProperties = state.editedProperties?.filter(
    e =>
      e.entityId !== payload.entityId ||
      (e.entityId === payload.entityId &&
        e.propertyName.toLocaleLowerCase() !== payload?.propertyName?.toLocaleLowerCase()),
  );
  state.editedProperties?.push({
    ...payload,
    propertyName: payload.propertyName,
    propertyValue: payload.propertyValue,
  });
};

const updateSelectedEntitiesConstruction = (
  state: ConstructionStateNew,
  {payload}: PayloadAction<SelectedEntity>,
): void => {
  const constructionElementsToUpdate: string[] = [payload.entityId];
  getChildConstructionElements(payload.entityId, constructionElementsToUpdate, state);

  constructionElementsToUpdate.forEach(elementId => {
    const foundElement = state.constructionElements.find(ce => ce.entityId === elementId);
    if(foundElement) {
      foundElement.isSelected = payload.isSelected;

      const deselectedEntityIndex = state.deselectedEntities.findIndex(e => e.entityId === elementId);
      if (deselectedEntityIndex !== undefined && deselectedEntityIndex !== -1) {
        state.deselectedEntities?.splice(deselectedEntityIndex, 1);
      } else {
        state.deselectedEntities?.push(foundElement);
      }
    }
  });
};

const addConstructionTags = (state: ConstructionStateNew, {payload}: PayloadAction<TagDto[]>): void => {
  if (state.data) {
    payload.forEach(tag => {
      const index = state.data?.tags.find(t => t.value === tag.value);
      if (index === undefined) {
        state.data?.tags.push(tag);
      }
    });
  }
};

const removeConstructionTag = (state: ConstructionStateNew, {payload}: PayloadAction<string>): void => {
  if (state.data) {
    state.data.tags = state.data?.tags.filter(t => t.value !== payload);
  }
};

export const constructionNewSlice = createSlice({
  name: "constructionNew",
  initialState,
  reducers: {
    load: loadConstruction,
    clear: clearConstruction,
    updateEditableProps: updateEditablePropsConstruction,
    updateSelectedEntities: updateSelectedEntitiesConstruction,
    addTags: addConstructionTags,
    removeTag: removeConstructionTag,
  },
});

export const {load, clear, updateEditableProps, updateSelectedEntities, addTags, removeTag} =
  constructionNewSlice.actions;

export default constructionNewSlice.reducer;
